import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { apiErrorMessage } from '../../lib/errors'
import { formatNumber, formatPercentage, formatPhraseList } from '../../lib/format'
import { NO_VIEW, type TableColumn } from '../../lib/tableView'
import { useTableView, type TableView } from '../../lib/useTableView'
import { DocumentsPanel } from '../documents/DocumentsPanel'
import { FmcsaVerificationPanel } from '../fmcsa/FmcsaVerificationPanel'
import { SharedContactsNotice } from '../sharedContacts/SharedContactsNotice'
import { useArrivedFromSave } from '../fmcsa/arrival'
import { ActivationBadge, ActivationPanel } from '../../components/ActivationPanel'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { InactiveToggle } from '../../components/InactiveToggle'
import { TableScroll } from '../../components/TableScroll'
import { historyRow } from '../../components/historyRow'
import { inactiveCount, rowsShown } from '../../lib/inactive'
import { ComplianceBadge } from '../brokers/ComplianceBadge'
import { COMPLIANCE_STATES, complianceRank } from '../brokers/compliance'
import type { ComplianceState } from '../brokers/api'
import { FleetStandingCell } from './FleetStandingCell'
import { formatMcNumber } from '../brokers/format'
import {
  getCarrier,
  TRAILER_TYPES,
  TRUCK_TYPES,
  type CarrierDetail,
  type DriverView,
  type TrailerView,
  type TruckView,
} from './api'

/** The three tables of a carrier's fleet. */
type FleetTable = 'drivers' | 'trucks' | 'trailers'

const NONE_SHOWN: Record<FleetTable, boolean> = { drivers: false, trucks: false, trailers: false }

/**
 * What each fleet table's columns sort and filter by (TRKC-110). The units we stopped working
 * with, when shown, stay after the ones in use whatever the column (TRKC-107): history never
 * sits among the units that can go.
 */
const inUseFirst = (unit: { active: boolean }) => (unit.active ? 0 : 1)

/** What the compliance column offers to filter by: the badge's own standings. */
const complianceColumn = <Row extends { complianceState: ComplianceState }>(): TableColumn<Row> => ({
  key: 'compliance',
  sortValue: (row) => complianceRank(row.complianceState),
  filter: { kind: 'choice', values: COMPLIANCE_STATES, matches: (row, state) => row.complianceState === state },
})

const driverName = (driver: DriverView) => `${driver.firstName} ${driver.lastName}`

const DRIVER_COLUMNS: readonly TableColumn<DriverView>[] = [
  { key: 'name', sortValue: driverName, filter: { kind: 'text', text: driverName } },
  { key: 'phone', sortValue: (driver) => driver.phone, filter: { kind: 'text', text: (driver) => driver.phone } },
  {
    key: 'cdl',
    sortValue: (driver) => driver.cdlNumber,
    filter: { kind: 'text', text: (driver) => [driver.cdlNumber ?? '', driver.cdlState ?? ''].join(' ') },
  },
  complianceColumn<DriverView>(),
]

/** A truck saved before cabs were asked has no type; the filter offers it as "not stated". */
const NOT_STATED = 'NotStated'

function truckColumns(t: TFunction): TableColumn<TruckView>[] {
  return [
    { key: 'unit', sortValue: (truck) => truck.unitNumber, filter: { kind: 'text', text: (truck) => truck.unitNumber } },
    {
      key: 'type',
      sortValue: (truck) => (truck.type === null ? null : t(`carriers.truckTypes.${truck.type}`)),
      filter: { kind: 'choice', values: [...TRUCK_TYPES, NOT_STATED], matches: (truck, type) => (truck.type ?? NOT_STATED) === type },
    },
    { key: 'vin', sortValue: (truck) => truck.vin, filter: { kind: 'text', text: (truck) => truck.vin } },
    { key: 'plate', sortValue: (truck) => truck.licensePlate, filter: { kind: 'text', text: (truck) => truck.licensePlate } },
    complianceColumn<TruckView>(),
  ]
}

function trailerColumns(t: TFunction): TableColumn<TrailerView>[] {
  return [
    {
      key: 'number',
      sortValue: (trailer) => trailer.trailerNumber,
      filter: { kind: 'text', text: (trailer) => trailer.trailerNumber },
    },
    {
      key: 'type',
      sortValue: (trailer) => t(`carriers.types.${trailer.type}`),
      filter: { kind: 'choice', values: TRAILER_TYPES, matches: (trailer, type) => trailer.type === type },
    },
    {
      key: 'pallets',
      sortValue: (trailer) => trailer.palletCapacity,
      filter: { kind: 'atLeast', value: (trailer) => trailer.palletCapacity, keepUnknown: false },
    },
    {
      key: 'weight',
      sortValue: (trailer) => trailer.weightCapacityPounds,
      filter: { kind: 'atLeast', value: (trailer) => trailer.weightCapacityPounds, keepUnknown: false },
    },
    complianceColumn<TrailerView>(),
  ]
}

/** The heading each column's filter box is named after, per table. */
const DRIVER_HEADINGS: Record<string, string> = {
  name: 'carriers.name',
  phone: 'carriers.phone',
  cdl: 'carriers.cdlNumber',
  compliance: 'carriers.compliance',
}
const TRUCK_HEADINGS: Record<string, string> = {
  unit: 'carriers.unitNumber',
  type: 'carriers.truckType',
  vin: 'carriers.vin',
  plate: 'carriers.licensePlate',
  compliance: 'carriers.compliance',
}
const TRAILER_HEADINGS: Record<string, string> = {
  number: 'carriers.trailerNumber',
  type: 'carriers.type',
  pallets: 'carriers.palletCapacity',
  weight: 'carriers.weightCapacity',
  compliance: 'carriers.compliance',
}

/**
 * One carrier in full: who it is, what it pays us, and the fleet it runs. This page
 * is the read for the whole family — drivers, trucks and trailers have forms of
 * their own but no pages: they are rows here.
 */
export function CarrierDetailPage() {
  const { t, i18n } = useTranslation()
  const { id } = useParams()

  const [carrier, setCarrier] = useState<CarrierDetail | null>(null)
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  // The form lands here after every save; that arrival, and only that, is when the
  // register is asked without anybody pressing the button (TRKC-79).
  const arrivedFromSave = useArrivedFromSave()
  // Each table holds its inactive units back until asked (TRKC-107). A re-read after
  // a paper or a stand-down keeps the choice; another carrier starts with none shown.
  const [showInactive, setShowInactive] = useState(NONE_SHOWN)
  // What each table's button shows or hides, for its aria-controls.
  const fleetId = useId()
  const fleetIdOf = (table: FleetTable) => `${fleetId}-${table}`

  // Each table sorts and filters by its columns (TRKC-110). The views live here, above the
  // page's early answers, and start over with another carrier, as the shown units do.
  const drivers = useMemo(() => rowsShown(carrier?.drivers ?? [], showInactive.drivers), [carrier, showInactive.drivers])
  const trucks = useMemo(() => rowsShown(carrier?.trucks ?? [], showInactive.trucks), [carrier, showInactive.trucks])
  const trailers = useMemo(() => rowsShown(carrier?.trailers ?? [], showInactive.trailers), [carrier, showInactive.trailers])
  const truckColumnsShown = useMemo(() => truckColumns(t), [t])
  const trailerColumnsShown = useMemo(() => trailerColumns(t), [t])
  const driverView = useTableView(drivers, DRIVER_COLUMNS, { rank: inUseFirst })
  const truckView = useTableView(trucks, truckColumnsShown, { rank: inUseFirst })
  const trailerView = useTableView(trailers, trailerColumnsShown, { rank: inUseFirst })
  const { replace: replaceDriverView } = driverView
  const { replace: replaceTruckView } = truckView
  const { replace: replaceTrailerView } = trailerView

  /**
   * Re-read, never patched in place. Standing a carrier down changes what is true of
   * every driver, truck and trailer below it — the server recomputes all of them —
   * and a screen that only updated the carrier's own chip would leave a fleet
   * underneath it still claiming to be available.
   */
  const load = useCallback(async () => {
    if (id === undefined) {
      return
    }

    try {
      setCarrier(await getCarrier(id))
      setLoadFailure(null)
    } catch (caught) {
      setLoadFailure({ error: caught })
    }
  }, [id])

  useEffect(() => {
    setCarrier(null)
    setLoadFailure(null)
    setShowInactive(NONE_SHOWN)
    replaceDriverView(NO_VIEW)
    replaceTruckView(NO_VIEW)
    replaceTrailerView(NO_VIEW)
    void load()
  }, [load, replaceDriverView, replaceTruckView, replaceTrailerView])

  if (loadFailure !== null) {
    return (
      <section className="page">
        <Breadcrumbs trail={[{ label: t('nav.carriers'), to: '/carriers' }, { label: null }]} />
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'carriers.loadError')}
        </p>
        <p>
          <Link to="/carriers">{t('carriers.backToList')}</Link>
        </p>
      </section>
    )
  }

  if (carrier === null) {
    return <p className="session-check">{t('carriers.loading')}</p>
  }

  const typeLabel = (type: string) => t(`carriers.types.${type}`)
  const toggle = (table: FleetTable) => () =>
    setShowInactive((shown) => ({ ...shown, [table]: !shown[table] }))

  return (
    <section className="page">
      <Breadcrumbs trail={[{ label: t('nav.carriers'), to: '/carriers' }, { label: carrier.name }]} />
      <header className="page__header page__header--row">
        <div>
          <h1>{carrier.name}</h1>
          <p className="page__subtitle">
            {formatMcNumber(carrier.mcNumber) ?? t('carriers.noMcNumber')}
            {' · '}
            {`${carrier.address.city}, ${carrier.address.state}`}
            {' · '}
            <ComplianceBadge state={carrier.complianceState} />
            {!carrier.active && ' '}
            <ActivationBadge active={carrier.active} />
          </p>
        </div>
        <Link className="button button--quiet" to={`/carriers/${carrier.id}/edit`}>
          {t('carriers.edit')}
        </Link>
      </header>

      {/* Judged on the saved version: every edit lands here, and is judged afresh (TRKC-99). */}
      <SharedContactsNotice kind="Carrier" id={carrier.id} version={carrier.updatedAt} />

      <div className="result">
        <h2>{t('carriers.commercial')}</h2>
        <dl className="result__grid">
          <div>
            <dt>{t('carriers.commissionRate')}</dt>
            <dd>{formatPercentage(carrier.commissionPercentage, i18n.language)}</dd>
          </div>
          <div>
            <dt>{t('carriers.usdotNumber')}</dt>
            <dd>{carrier.usdotNumber ?? '—'}</dd>
          </div>
          <div>
            <dt>{t('carriers.phone')}</dt>
            <dd>{carrier.phone ?? '—'}</dd>
          </div>
          <div>
            <dt>{t('carriers.email')}</dt>
            <dd className="detail__email">{carrier.email ?? '—'}</dd>
          </div>
        </dl>
      </div>

      <section className="fleet">
        <header className="page__header--row fleet__header">
          <h2>{t('carriers.drivers')}</h2>
          <Link className="button button--quiet" to={`/carriers/${carrier.id}/drivers/new`}>
            {t('carriers.addDriver')}
          </Link>
        </header>

        <div id={fleetIdOf('drivers')}>
          {carrier.drivers.length === 0 ? (
            <p className="placeholder">{t('carriers.noDrivers')}</p>
          ) : drivers.length === 0 ? (
            <p className="placeholder">{t('carriers.noDriversInUse')}</p>
          ) : (
            <TableScroll label={t('carriers.drivers')}>
              <table className="table">
                <caption className="visually-hidden">{t('carriers.drivers')}</caption>
                <FleetHeads view={driverView} columns={DRIVER_COLUMNS} headings={DRIVER_HEADINGS} />
                <tbody>
                  <NoneMatch view={driverView} columns={DRIVER_COLUMNS.length} />
                  {driverView.rows.map((driver) => (
                    <tr key={driver.id} className={historyRow(!driver.active)}>
                      <td>
                        <Link to={`/carriers/${carrier.id}/drivers/${driver.id}`}>
                          {`${driver.firstName} ${driver.lastName}`}
                        </Link>
                      </td>
                      <td>{driver.phone ?? '—'}</td>
                      <td className="table__numeric">
                        {driver.cdlNumber === null
                          ? '—'
                          : driver.cdlState === null
                            ? driver.cdlNumber
                            : t('carriers.cdlLicense', { number: driver.cdlNumber, state: driver.cdlState })}
                      </td>
                      <td>
                        <FleetStandingCell record={driver} carrier={carrier} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </div>
        <FilterSummary view={driverView} />
        <InactiveToggle
          kind="drivers"
          controls={fleetIdOf('drivers')}
          count={inactiveCount(carrier.drivers)}
          shown={showInactive.drivers}
          onToggle={toggle('drivers')}
        />
      </section>

      <section className="fleet">
        <header className="page__header--row fleet__header">
          <h2>{t('carriers.trucks')}</h2>
          <Link className="button button--quiet" to={`/carriers/${carrier.id}/trucks/new`}>
            {t('carriers.addTruck')}
          </Link>
        </header>

        <div id={fleetIdOf('trucks')}>
          {carrier.trucks.length === 0 ? (
            <p className="placeholder">{t('carriers.noTrucks')}</p>
          ) : trucks.length === 0 ? (
            <p className="placeholder">{t('carriers.noTrucksInUse')}</p>
          ) : (
            <TableScroll label={t('carriers.trucks')}>
              <table className="table">
                <caption className="visually-hidden">{t('carriers.trucks')}</caption>
                <FleetHeads
                  view={truckView}
                  columns={truckColumnsShown}
                  headings={TRUCK_HEADINGS}
                  options={{
                    type: [
                      ...TRUCK_TYPES.map((type) => ({ value: type, label: t(`carriers.truckTypes.${type}`) })),
                      { value: NOT_STATED, label: t('carriers.notStated') },
                    ],
                  }}
                />
                <tbody>
                  <NoneMatch view={truckView} columns={truckColumnsShown.length} />
                  {truckView.rows.map((truck) => (
                    <tr key={truck.id} className={historyRow(!truck.active)}>
                      <td>
                        <Link to={`/carriers/${carrier.id}/trucks/${truck.id}`}>
                          {truck.unitNumber}
                        </Link>
                      </td>
                      <td>
                        {/* A truck saved before cabs were asked says so until its next save (TRKC-102). */}
                        {truck.type === null ? (
                          <span className="muted">{t('carriers.notStated')}</span>
                        ) : (
                          t(`carriers.truckTypes.${truck.type}`)
                        )}
                        {/* A straight box truck's box and features, said as a trailer's are (TRKC-100, TRKC-102). */}
                        {truck.boxLengthFeet !== null && (
                          <span className="muted">
                            {' · '}
                            {t('carriers.feet', { feet: truck.boxLengthFeet })}
                          </span>
                        )}
                        {truck.features.length > 0 && (
                          <span className="muted">
                            {' · '}
                            {formatPhraseList(
                              truck.features.map((feature) => t(`equipment.features.${feature}`)),
                              i18n.language,
                              true,
                            )}
                          </span>
                        )}
                      </td>
                      <td className="table__numeric">{truck.vin ?? '—'}</td>
                      <td className="table__numeric">{truck.licensePlate ?? '—'}</td>
                      <td>
                        <FleetStandingCell record={truck} carrier={carrier} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </div>
        <FilterSummary view={truckView} />
        <InactiveToggle
          kind="trucks"
          controls={fleetIdOf('trucks')}
          count={inactiveCount(carrier.trucks)}
          shown={showInactive.trucks}
          onToggle={toggle('trucks')}
        />
      </section>

      <section className="fleet">
        <header className="page__header--row fleet__header">
          <h2>{t('carriers.trailers')}</h2>
          <Link className="button button--quiet" to={`/carriers/${carrier.id}/trailers/new`}>
            {t('carriers.addTrailer')}
          </Link>
        </header>

        <div id={fleetIdOf('trailers')}>
          {carrier.trailers.length === 0 ? (
            <p className="placeholder">{t('carriers.noTrailers')}</p>
          ) : trailers.length === 0 ? (
            <p className="placeholder">{t('carriers.noTrailersInUse')}</p>
          ) : (
            <TableScroll label={t('carriers.trailers')}>
              <table className="table">
                <caption className="visually-hidden">{t('carriers.trailers')}</caption>
                <FleetHeads
                  view={trailerView}
                  columns={trailerColumnsShown}
                  headings={TRAILER_HEADINGS}
                  options={{ type: TRAILER_TYPES.map((type) => ({ value: type, label: typeLabel(type) })) }}
                />
                <tbody>
                  <NoneMatch view={trailerView} columns={trailerColumnsShown.length} />
                  {trailerView.rows.map((trailer) => (
                    <tr key={trailer.id} className={historyRow(!trailer.active)}>
                      <td>
                        <Link to={`/carriers/${carrier.id}/trailers/${trailer.id}`}>
                          {trailer.trailerNumber}
                        </Link>
                      </td>
                      <td>
                        {typeLabel(trailer.type)}
                        {/* What a load can ask for and this trailer offers (TRKC-100). */}
                        {trailer.features.length > 0 && (
                          <span className="muted">
                            {' · '}
                            {formatPhraseList(
                              trailer.features.map((feature) => t(`equipment.features.${feature}`)),
                              i18n.language,
                              true,
                            )}
                          </span>
                        )}
                      </td>
                      <td className="table__numeric">{trailer.palletCapacity ?? '—'}</td>
                      <td className="table__numeric">
                        {trailer.weightCapacityPounds === null
                          ? '—'
                          : `${formatNumber(trailer.weightCapacityPounds, i18n.language)} lb`}
                      </td>
                      <td>
                        <FleetStandingCell record={trailer} carrier={carrier} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </div>
        <FilterSummary view={trailerView} />
        <InactiveToggle
          kind="trailers"
          controls={fleetIdOf('trailers')}
          count={inactiveCount(carrier.trailers)}
          shown={showInactive.trailers}
          onToggle={toggle('trailers')}
        />
      </section>

      <FmcsaVerificationPanel
        ownerType="Carrier"
        ownerId={carrier.id}
        storedVerification={carrier.fmcsaVerification}
        hasIdentifiers={carrier.mcNumber !== null || carrier.usdotNumber !== null}
        askOnMount={arrivedFromSave}
      />

      {/* Filing a paper here can block this carrier and every driver, truck and
          trailer under it. The read is the only thing that knows. */}
      <DocumentsPanel
        ownerType="Carrier"
        ownerId={carrier.id}
        onOwnerStandingChanged={() => void load()}
      />

      <ActivationPanel
        ownerType="Carrier"
        ownerId={carrier.id}
        active={carrier.active}
        deactivatedAt={carrier.deactivatedAt}
        cascadeWarningKey="activation.cascadeCarrier"
        onChanged={() => void load()}
      />

      <p>
        <Link to="/carriers">{t('carriers.backToList')}</Link>
      </p>
    </section>
  )
}

/** A fleet table's headings and its row of filters (TRKC-110). */
function FleetHeads<Row>({
  view,
  columns,
  headings,
  options = {},
}: {
  view: TableView<Row>
  columns: readonly TableColumn<Row>[]
  headings: Record<string, string>
  options?: Record<string, readonly { value: string; label: string }[]>
}) {
  const { t } = useTranslation()
  const complianceOptions = COMPLIANCE_STATES.map((state) => ({ value: state, label: t(`compliance.${state.toLowerCase()}`) }))

  return (
    <thead>
      <tr>
        {columns.map((column) => (
          <SortableHeader key={column.key} view={view} column={column.key}>
            {t(headings[column.key] ?? column.key)}
          </SortableHeader>
        ))}
      </tr>
      <tr className="table__filters">
        {columns.map((column) => (
          <FilterCell
            key={column.key}
            view={view}
            column={column}
            label={t(headings[column.key] ?? column.key)}
            options={column.key === 'compliance' ? complianceOptions : options[column.key]}
          />
        ))}
      </tr>
    </thead>
  )
}

/** The line a table shows when its filters keep none of its rows. */
function NoneMatch<Row>({ view, columns }: { view: TableView<Row>; columns: number }) {
  const { t } = useTranslation()

  return view.rows.length === 0 ? (
    <tr>
      <td colSpan={columns} className="placeholder">
        {t('table.noneMatch')}
      </td>
    </tr>
  ) : null
}
